import { useState, useRef } from 'react'
import { Input, ColorPicker } from '@/components/ui'
import { useBranding } from '@/contexts'
import type { BrandingConfig } from '@/types'

interface BrandingPanelProps {
  isOpen: boolean
  onClose: () => void
}

const FONT_OPTIONS: { value: BrandingConfig['fontFamily']; label: string }[] = [
  { value: 'default', label: 'Default (Source Sans 3)' },
  { value: 'inter', label: 'Inter' },
  { value: 'montserrat', label: 'Montserrat' },
  { value: 'poppins', label: 'Poppins' },
  { value: 'lato', label: 'Lato' },
]

export default function BrandingPanel({ isOpen, onClose }: BrandingPanelProps) {
  const { branding, updateBranding, resetBranding, setTheme } = useBranding()
  const [logoPreview, setLogoPreview] = useState<string | null>(branding.logo)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64 = reader.result as string
        setLogoPreview(base64)
        updateBranding({ logo: base64 })
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveLogo = () => {
    setLogoPreview(null)
    updateBranding({ logo: null })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="
        fixed right-4 top-24
        w-80
        bg-brand-dark
        border border-white/10
        rounded-xl
        shadow-2xl
        z-50
        overflow-hidden
      "
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/[0.02]">
        <h3 className="text-sm font-semibold text-brand-light">Branding Settings</h3>
        <button
          onClick={onClose}
          className="p-1 text-brand-muted hover:text-brand-light rounded transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="p-4 space-y-5 max-h-[calc(100vh-200px)] overflow-y-auto">
        {/* Theme Toggle */}
        <div>
          <label className="block text-sm font-medium text-brand-muted mb-2">Report Theme</label>
          <div className="flex gap-2">
            <button
              onClick={() => setTheme('dark')}
              className={`
                flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all
                ${branding.theme === 'dark'
                  ? 'bg-accent-teal text-brand-dark'
                  : 'bg-white/[0.03] text-brand-muted hover:bg-white/[0.06]'}
              `}
            >
              Dark
            </button>
            <button
              onClick={() => setTheme('light')}
              className={`
                flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all
                ${branding.theme === 'light'
                  ? 'bg-accent-teal text-brand-dark'
                  : 'bg-white/[0.03] text-brand-muted hover:bg-white/[0.06]'}
              `}
            >
              Light
            </button>
          </div>
        </div>

        {/* Company Name */}
        <div>
          <Input
            label="Company Name"
            value={branding.companyName}
            onChange={(e) => updateBranding({ companyName: e.target.value })}
            placeholder="Your Company Name"
          />
        </div>

        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-brand-muted mb-2">Company Logo</label>
          {logoPreview ? (
            <div className="flex items-center gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/10">
              <img
                src={logoPreview}
                alt="Logo preview"
                className="w-12 h-12 object-contain rounded"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-brand-light truncate">Logo uploaded</p>
                <button
                  onClick={handleRemoveLogo}
                  className="text-xs text-red-400 hover:text-red-300"
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <label className="
              flex flex-col items-center justify-center
              p-4
              bg-white/[0.03]
              border border-dashed border-white/20
              rounded-lg
              cursor-pointer
              hover:border-accent-teal/50
              transition-colors
            ">
              <svg className="w-8 h-8 text-brand-muted mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-sm text-brand-muted">Click to upload logo</span>
              <span className="text-xs text-brand-subtle mt-1">PNG, JPG, SVG</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* Colors */}
        <div className="space-y-4">
          <ColorPicker
            label="Primary Color"
            value={branding.primaryColor}
            onChange={(color) => updateBranding({ primaryColor: color })}
          />
          <ColorPicker
            label="Secondary Color"
            value={branding.secondaryColor}
            onChange={(color) => updateBranding({ secondaryColor: color })}
          />
        </div>

        {/* Font Family */}
        <div>
          <label className="block text-sm font-medium text-brand-muted mb-2">Font Family</label>
          <select
            value={branding.fontFamily}
            onChange={(e) => updateBranding({ fontFamily: e.target.value as BrandingConfig['fontFamily'] })}
            className="
              w-full
              px-4 py-3
              bg-white/[0.03]
              border border-white/10
              rounded-lg
              text-sm text-brand-light
              focus:outline-none focus:border-accent-teal
              cursor-pointer
            "
          >
            {FONT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Reset Button */}
        <button
          onClick={resetBranding}
          className="
            w-full
            py-2
            text-sm
            text-red-400
            hover:text-red-300
            hover:bg-red-400/10
            rounded-lg
            transition-colors
          "
        >
          Reset to Defaults
        </button>
      </div>
    </div>
  )
}
